import { useState, useEffect, useRef } from 'react';
import { DialogShell } from './ui/DialogShell';
import { CountrySelector } from './location/CountrySelector';
import type { ProvinciaEclesiastica } from '../services';
import type { Country } from '../types/location';
import { derivarContinente } from '../utils/continenteUtils';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { FormErrorAlert } from './ui/FormErrorAlert';
import { ModalFormActions } from './ui/ModalFormActions';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';

interface ProvinciaEclesiasticaModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSave: (provinciaEclesiastica: Omit<ProvinciaEclesiastica, 'id' | 'created_at'>) => Promise<void>;
  readonly provinciaEclesiastica?: ProvinciaEclesiastica;
}

export function ProvinciaEclesiasticaModal({ isOpen, onClose, onSave, provinciaEclesiastica }: ProvinciaEclesiasticaModalProps) {
  const [formData, setFormData] = useState({
    nombre: '',
    codigo_iso: '',
    continente: '' as string,
    activo: true
  });
  const [selectedCountry, setSelectedCountry] = useState<Pick<Country, 'id' | 'name' | 'iso2'> | null>(null);
  const [loading, setLoading] = useState(false);

  // Hook centralizado para manejo de errores
  const { error, clearError, handleError } = useErrorHandler();

  const hasFormChanges = (): boolean => {
    if (!provinciaEclesiastica) {
      return formData.codigo_iso !== '';
    }
    return formData.codigo_iso !== (provinciaEclesiastica.codigo_iso || '') ||
      formData.activo !== provinciaEclesiastica.activo;
  };

  useEffect(() => {
    if (isOpen && provinciaEclesiastica) {
      setFormData({
        nombre: provinciaEclesiastica.nombre || '',
        codigo_iso: provinciaEclesiastica.codigo_iso || '',
        continente: provinciaEclesiastica.continente || '',
        activo: provinciaEclesiastica.activo
      });
      // Pre-seleccionar país para el CountrySelector usando codigo_iso
      if (provinciaEclesiastica.codigo_iso) {
        setSelectedCountry({ id: '', name: '', iso2: provinciaEclesiastica.codigo_iso });
      } else {
        setSelectedCountry(null);
      }
    } else if (isOpen) {
      setFormData({
        nombre: '',
        codigo_iso: '',
        continente: '',
        activo: true
      });
      setSelectedCountry(null);
    }
  }, [provinciaEclesiastica, isOpen]);

  const validate = (): boolean => {
    if (!formData.codigo_iso.trim()) {
      handleError('Debe seleccionar un país de la lista');
      return false;
    }
    return true;
  };

  const handleCountryChange = (country: Country | null) => {
    if (country) {
      const continente = derivarContinente(country.region, country.subregion);
      setSelectedCountry(country);
      setFormData({
        ...formData,
        codigo_iso: country.iso2,
        nombre: country.nombre_es || country.name,
        continente,
      });
    } else {
      setSelectedCountry(null);
      setFormData({ ...formData, codigo_iso: '', nombre: '', continente: '' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      setLoading(true);
      
      const dataToSave = {
        ...formData,
        continente: formData.continente || null
      };
      
      await onSave(dataToSave);
      onClose();
    } catch (error) {
      console.error('Error al guardar provincia eclesiástica:', error);
      reportarErrorInesperado(error, 'Error al guardar la provincia eclesiástica');
    } finally {
      setLoading(false);
    }
  };

  const closeRef = useRef<(() => void) | null>(null);
  const requestClose = () => closeRef.current?.();

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={onClose}
      hasUnsavedChanges={hasFormChanges()}
      closeRef={closeRef}
      title={provinciaEclesiastica ? 'Editar Provincia Eclesiástica' : 'Agregar Provincia Eclesiástica'}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <CountrySelector
            label="País"
            value={selectedCountry}
            onChange={handleCountryChange}
            placeholder="Seleccione un país..."
            required={true}
            error={error ? 'Debe seleccionar un país de la lista' : undefined}
          />
        </div>

        {formData.continente && (
          <div>
            <span className="block text-sm font-medium text-slate-700 mb-1">
              Continente
            </span>
            <p className="text-sm text-slate-600 bg-slate-50 rounded px-3 py-2">
              {formData.continente}
            </p>
          </div>
        )}

        {/* Mostrar checkbox solo para editar provincias existentes */}
        {provinciaEclesiastica && (
          <div className="flex items-center">
            <input
              type="checkbox"
              id="activo"
              checked={formData.activo}
              onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
              className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="activo" className="ml-2 text-sm text-slate-700">
              Provincia Eclesiástica activa
            </label>
          </div>
        )}

        <FormErrorAlert error={error} onClear={clearError} />

        <ModalFormActions
            onCancel={requestClose}
            loading={loading}
            submitLabel={provinciaEclesiastica ? 'Actualizar' : 'Crear'}
          />
      </form>
    </DialogShell>
  );
}
